import { isEqual } from "es-toolkit";
import { useEffect, useMemo, useRef } from "react";
import type { DefaultValues, FieldValues, Path } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";
import { Button } from "@songtradr-one/components-frontend";
import { FormField } from "../form-field/FormField";
import {
  CustomFieldContainer,
  Divider,
  FormActionsContainer,
  FormActionsRight,
  FormFieldContainer,
  type FormProps,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionSubtitle,
  FormSectionTitle,
  FormSectionWrapper,
  FormWrapper,
  buildFormFieldProps,
  convertFieldsToSections,
  removeTypename,
  text,
} from "./Form.Parts";
import type { Field } from "./Form.Parts";
import { useFormNavigationGuard } from "./use-form-navigation-guard/useFormNavigationGuard";
import type { NavigationGuardConfig } from "./use-form-navigation-guard/useFormNavigationGuard.Parts";

export const Form = <T extends FieldValues>({
  fields,
  sections,
  defaultValues,
  onSubmit,
  onValuesChange,
  options = {},
  layout,
  submitButton,
  cancelButton,
  actions,
  children,
  preventNavigationOnChanges: preventNavigation,
}: FormProps<T>) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting, isValid },
  } = useForm<T>({
    mode: "onTouched",
    defaultValues,
  });

  // Track the baseline values for comparing changes (updated after save)
  const baselineRef = useRef<DefaultValues<T> | undefined>(defaultValues);

  // Watch for form value changes and call onValuesChange (using subscription to prevent infinite loops)
  useEffect(() => {
    if (onValuesChange) {
      const subscription = watch((value) => {
        onValuesChange(value as T);
      });
      return () => subscription.unsubscribe();
    }
  }, [onValuesChange, watch]);

  const calculateChanges = (rawData: T) => {
    const { __typename, ...cleanData } = rawData;
    const cleanDefaults = baselineRef.current
      ? { ...baselineRef.current }
      : ({} as T);
    if ("__typename" in cleanDefaults) {
      delete cleanDefaults.__typename;
    }

    const changes: Partial<Omit<T, "__typename">> = {};
    const changedFields: string[] = [];

    Object.keys(cleanData).forEach((key) => {
      const currentValue = cleanData[key as keyof typeof cleanData];
      const defaultValue = cleanDefaults[key as keyof typeof cleanDefaults];

      const hasChanged = !isEqual(currentValue, defaultValue);

      if (hasChanged) {
        changes[key as keyof Omit<T, "__typename">] = currentValue;
        changedFields.push(key);
      }
    });

    return {
      cleanData: cleanData as Omit<T, "__typename">,
      changes,
      rawData,
      changedFields,
    };
  };

  const submitHandler = handleSubmit(async (rawData: T) => {
    const submissionData = calculateChanges(rawData);

    // Merge form data with non-form fields from defaultValues
    const allFieldNames = new Set([
      ...(fields || [])
        .filter((f) => f.type !== "divider" && f.type !== "sectionHeader")
        .map((f) => (f as { name: string }).name),
      ...(sections || []).flatMap((s) =>
        s.fields
          .filter(
            (f) =>
              f.type !== "divider" &&
              f.type !== "sectionHeader" &&
              f.type !== "custom",
          )
          .map((f) => f.name as string),
      ),
    ]);

    const allTopLevelFieldNames = new Set(
      [...allFieldNames].map((name) => name.split(".")[0]),
    );

    const preservedFields: Record<string, unknown> = {};
    if (defaultValues) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        if (!allTopLevelFieldNames.has(key)) {
          preservedFields[key] = value;
        }
      });
    }

    // Create complete object with preserved fields + form data
    const completeData = {
      ...preservedFields,
      ...submissionData.cleanData,
    } as T;
    const completeDataWithoutTypename = removeTypename<T>(completeData);

    try {
      await onSubmit?.({
        ...submissionData,
        cleanData: completeDataWithoutTypename,
      });
    } catch {
      // Consumer reported the failure; keep the form dirty so it can be retried
      return;
    }

    // Update baseline and reset form with current values to clear hasChanges state after successful submit
    baselineRef.current = rawData as DefaultValues<T>;
    reset(rawData);
  });

  // Watch form data to track changes for submit button state
  const watchedData = useWatch({ control });

  const hasChanges = useMemo(() => {
    if (!watchedData) return false;

    if (!baselineRef.current) {
      return Object.values(watchedData).some((value) => {
        if (typeof value === "string") return value.trim() !== "";
        if (Array.isArray(value)) return value.length > 0;
        return value != null;
      });
    }

    const { changedFields } = calculateChanges(watchedData as T);
    return changedFields.length > 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedData]);

  // Navigation guard to prevent leaving with unsaved changes
  const navigationGuardConfig: NavigationGuardConfig =
    typeof preventNavigation === "object"
      ? preventNavigation
      : { enabled: !!preventNavigation };

  useFormNavigationGuard(hasChanges, navigationGuardConfig);

  const formSections = convertFieldsToSections(fields, sections);

  const renderField = (
    field: Field<T>,
    sectionColumns: number,
    index: number,
  ) => {
    if (field.visible && !field.visible(watchedData as T)) {
      return null;
    }

    if (field.type === "divider") {
      return (
        <FormFieldContainer
          key={`divider-${index}`}
          $columnSpan={sectionColumns}
          $columns={sectionColumns}
        >
          <Divider $shade={field.shade} $variant={field.variant || "normal"} />
        </FormFieldContainer>
      );
    }

    if (field.type === "sectionHeader") {
      return (
        <FormFieldContainer
          key={`sectionHeader-${index}`}
          $columnSpan={field.columnSpan || sectionColumns}
          $columns={sectionColumns}
        >
          <FormSectionHeader>{field.label}</FormSectionHeader>
        </FormFieldContainer>
      );
    }

    if (field.type === "custom") {
      const content =
        typeof field.content === "function"
          ? field.content({ control })
          : field.content;
      return (
        <FormFieldContainer
          key={`custom-${index}`}
          $columnSpan={field.columnSpan || sectionColumns}
          $columns={sectionColumns}
        >
          <CustomFieldContainer>{content}</CustomFieldContainer>
        </FormFieldContainer>
      );
    }

    const formFieldProps = buildFormFieldProps<T>(
      field as Exclude<
        Field<T>,
        { type: "divider" | "sectionHeader" | "custom" }
      >,
      sectionColumns,
      control,
      register,
      errors,
      options,
      defaultValues,
    );

    return (
      <FormFieldContainer
        key={field.name}
        $columnSpan={field.columnSpan || (field.single ? sectionColumns : 1)}
        $columns={sectionColumns}
      >
        <FormField<T, Path<T>> {...formFieldProps} />
      </FormFieldContainer>
    );
  };

  return (
    <FormWrapper $maxWidth={layout?.maxWidth}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          return submitHandler(e);
        }}
      >
        {formSections.map((section, sectionIndex) => {
          const sectionColumns = section.columns || layout?.columns || 2;

          return (
            <FormSectionWrapper
              key={sectionIndex}
              $columns={sectionColumns}
              $gap={section.gap || layout?.gap}
            >
              {section.title && (
                <FormSectionTitle $margin={sectionIndex > 0}>
                  {section.title}
                </FormSectionTitle>
              )}
              {section.subtitle && (
                <FormSectionSubtitle>{section.subtitle}</FormSectionSubtitle>
              )}
              {section.description && (
                <FormSectionDescription>
                  {section.description}
                </FormSectionDescription>
              )}
              {section.fields.map((field, fieldIndex) =>
                renderField(field, sectionColumns, fieldIndex),
              )}
            </FormSectionWrapper>
          );
        })}

        {/* Form Actions */}
        {(submitButton || cancelButton || actions) && (
          <FormActionsContainer>
            {actions}
            <FormActionsRight>
              {cancelButton && (
                <Button
                  type="button"
                  onClick={cancelButton.onClick}
                  disabled={cancelButton.disabled || isSubmitting}
                  $variant={cancelButton.variant || "basic"}
                  $size={cancelButton.size || "normal"}
                >
                  {cancelButton.text || text.cancel}
                </Button>
              )}
              {submitButton && (
                <Button
                  type="submit"
                  disabled={
                    submitButton.disabled ||
                    (submitButton.disableWhenUnchanged !== false &&
                      !hasChanges) ||
                    (submitButton.disableWhenInvalid && !isValid)
                  }
                  $variant={submitButton.variant || "primary"}
                  $size={submitButton.size || "normal"}
                  loading={submitButton.loading || isSubmitting}
                >
                  {submitButton.text || text.submit}
                </Button>
              )}
            </FormActionsRight>
          </FormActionsContainer>
        )}

        {children}
      </form>
    </FormWrapper>
  );
};
